import {
  useCallback, useEffect, useId, useRef,
  type ClipboardEvent, type InputHTMLAttributes, type KeyboardEvent,
} from 'react'

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string | null
  hint?: string
}

export function Field({ label, error, hint, id, className, ...rest }: FieldProps) {
  const auto = useId()
  const inputId = id ?? auto
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`

  return (
    <label
      className={['field', error ? 'field--invalid' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      htmlFor={inputId}
    >
      <span className="field__label kicker">{label}</span>
      <input
        id={inputId}
        className="field__input"
        aria-invalid={error ? true : undefined}
        // The hint is described too, not just the error — otherwise a screen-reader
        // user never hears what the field expects.
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        {...rest}
      />
      {error ? (
        <span id={errorId} className="field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        // --ink-muted, not --ink-faint: faint is 3.2:1 on paper and fails WCAG AA for
        // text this size. Muted is 5.8:1.
        <span id={hintId} className="field__hint">
          {hint}
        </span>
      ) : null}
    </label>
  )
}

interface OtpInputProps {
  value: string
  onChange: (next: string) => void
  length?: number
  /** Fired once the last digit lands, so the user never hunts for a submit button. */
  onComplete?: (code: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

/**
 * Six separate boxes rather than one text field: it makes the expected shape of the
 * code obvious, and lets the numeric keypad stay up between digits on mobile.
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  onComplete,
  disabled,
  autoFocus,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const completed = useRef(false)

  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  const focusAt = useCallback((index: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, index))]
    el?.focus()
    el?.select()
  }, [length])

  useEffect(() => {
    if (value.length === length && !completed.current) {
      completed.current = true
      onComplete?.(value)
    }
    if (value.length < length) completed.current = false
  }, [value, length, onComplete])

  function setDigit(index: number, digit: string) {
    const chars = value.padEnd(length, ' ').slice(0, length).split('')
    chars[index] = digit || ' '
    onChange(chars.join('').replace(/\s+$/g, '').trimEnd())
  }

  function handleChange(index: number, raw: string) {
    const only = raw.replace(/\D/g, '')
    if (!only) {
      setDigit(index, '')
      return
    }
    // Pasting or fast typing can deliver several digits into one box.
    if (only.length > 1) {
      const chars = value.padEnd(length, ' ').split('')
      for (let i = 0; i < only.length && index + i < length; i++) {
        chars[index + i] = only[i]
      }
      onChange(chars.join('').trimEnd())
      focusAt(index + only.length)
      return
    }
    setDigit(index, only)
    focusAt(index + 1)
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digits[index].trim()) {
        setDigit(index, '')
      } else {
        setDigit(index - 1, '')
        focusAt(index - 1)
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusAt(index - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focusAt(index + 1)
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const only = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (only) {
      onChange(only)
      focusAt(only.length)
    }
  }

  return (
    <div className="otp" role="group" aria-label={`${length}-digit code`}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          className="otp__cell"
          value={digits[i].trim()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          inputMode="numeric"
          // `one-time-code` lets iOS and Android offer the SMS/email code directly.
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  )
}
